import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
    format,
    startOfWeek,
    startOfMonth,
    startOfQuarter,
    eachDayOfInterval,
    eachWeekOfInterval,
    eachMonthOfInterval,
    eachQuarterOfInterval,
    getQuarter,
} from "date-fns";
import type { InternalTransfer } from "@/hooks/finance/useInternalTransfers";
import { paginateSupabase } from "@/lib/supabase-paginate";

import {
    useOpeningBalancesMatrixRange,
    type ConvertedOpeningBalanceDay,
    type AccountBalanceDetail,
} from "@/hooks/finance/reconciliation/useStartingBalance";

export type ViewPeriod = "daily" | "weekly" | "monthly" | "quarterly";
export type DisplayCurrency = "ETB" | "USD";

interface ExchangeRateEntry {
    rate: number;
    effective_date: string;
    rate_type: string;
}

export interface CashFlowRow {
    key: string;
    label: string;
    isHeader?: boolean;
    isTotal?: boolean;
    indent?: number;
    values: Map<string, number>;
    children?: CashFlowRow[];
    categoryId?: string;
}

export type PeriodMeta = {
    isPartial: boolean;

    includedAccounts: AccountBalanceDetail[];

    missingFxAccountsCount: number;
    missingFxAmountByCurrency: Record<string, number>;
    missingFxAccounts: AccountBalanceDetail[];

    missingSourceAccountsCount: number;
    missingSourceAmountByCurrency: Record<string, number>;
    missingSourceAccounts: AccountBalanceDetail[];
};

export function useCashFlowStatementDetails(
    dateFrom: string,
    dateTo: string,
    viewPeriod: ViewPeriod = "daily",
    displayCurrency: DisplayCurrency = "ETB"
) {
    const { user } = useAuth();

    const exchangeRatesQuery = useQuery({
        queryKey: ["cash-flow-details-exchange-rates", dateFrom, dateTo],
        queryFn: async () => {
            const data = await paginateSupabase<any>((from, to) =>
                supabase
                    .from("exchange_rates" as any)
                    .select("rate, effective_date, rate_type, currency_pair")
                    .in("currency_pair", ["USD/ETB", "EUR/ETB", "GBP/ETB", "USD/EUR", "EUR/USD", "USD/GBP", "GBP/USD", "EUR/GBP", "GBP/EUR"])
                    .lte("effective_date", dateTo)
                    .order("effective_date", { ascending: false })
                    .range(from, to) as any
            );

            return (data || []) as unknown as (ExchangeRateEntry & {
                currency_pair: string;
            })[];
        },
        enabled: !!user && !!dateFrom && !!dateTo,
    });

    const expensesQuery = useQuery({
        queryKey: ["cash-flow-details-expenses", dateFrom, dateTo],
        queryFn: async () => {
            return await paginateSupabase<any>((from, to) =>
                supabase
                    .from("expenses")
                    .select(
                        "id, expense_date, amount, total_amount, net_payment, currency, payment_status, category_id, bank_account_id, category:expense_categories(id, name, cash_flow_type, category_type, parent_id)"
                    )
                    .gte("expense_date", dateFrom)
                    .lte("expense_date", dateTo)
                    .eq("payment_status", "paid")
                    .range(from, to)
            );
        },
        enabled: !!user,
    });

    const categoriesQuery = useQuery({
        queryKey: ["cash-flow-details-categories"],
        queryFn: async () => {
            return await paginateSupabase<any>((from, to) =>
                supabase
                    .from("expense_categories")
                    .select("id, name, cash_flow_type, category_type, parent_id")
                    .range(from, to)
            );
        },
        enabled: !!user,
    });

    const salesQuery = useQuery({
        queryKey: ["cash-flow-details-chipchip-gmv", dateFrom, dateTo],
        queryFn: async () => {
            const rows = await paginateSupabase<{ date: string; total_gmv: number }>((from, to) =>
                (supabase as any)
                    .from("chipchip_daily_accepted_totals")
                    .select("date, total_gmv")
                    .gte("date", dateFrom)
                    .lte("date", dateTo)
                    .range(from, to)
            );
            return rows.map((r) => ({
                sale_date: r.date,
                total_revenue: Number(r.total_gmv) || 0,
            }));
        },
        enabled: !!user && !!dateFrom && !!dateTo,
    });

    const b2bQuery = useQuery({
        queryKey: ["cash-flow-details-b2b-credit", dateFrom, dateTo],
        queryFn: async () => {
            return await paginateSupabase<any>((from, to) =>
                (supabase as any)
                    .from("b2b_daily_credit_collections")
                    .select(
                        "id, collection_date, credit_collected, credit_collected_orders"
                    )
                    .gte("collection_date", dateFrom)
                    .lte("collection_date", dateTo)
                    .range(from, to)
            );
        },
        enabled: !!user,
    });

    // ChipChip Other Revenue (Delivery + Packaging fees) — separate from product sales
    const chipchipFeesQuery = useQuery({
        queryKey: ["cash-flow-details-chipchip-fees", dateFrom, dateTo],
        queryFn: async () => {
            return await paginateSupabase<{ date: string; packaging_fee_total: number; delivery_charge_total: number; combined_total: number }>((from, to) =>
                (supabase as any)
                    .from("chipchip_daily_revenue_fees")
                    .select("date, packaging_fee_total, delivery_charge_total, combined_total")
                    .gte("date", dateFrom)
                    .lte("date", dateTo)
                    .range(from, to)
            );
        },
        enabled: !!user && !!dateFrom && !!dateTo,
    });

    const bankAccountsQuery = useQuery({
        queryKey: ["cash-flow-details-bank-accounts"],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("bank_accounts")
                .select("id, name, currency, exchange_rate_type")
                .eq("is_active", true);

            if (error) throw error;
            return data || [];
        },
        enabled: !!user,
    });

    // Fetch internal transfers
    const transfersQuery = useQuery({
        queryKey: ["cash-flow-details-internal-transfers", dateFrom, dateTo],
        queryFn: async () => {
            const rows = await paginateSupabase<any>((from, to) =>
                supabase
                    .from("internal_transfers" as any)
                    .select("*")
                    .gte("transfer_date", dateFrom)
                    .lte("transfer_date", dateTo)
                    .range(from, to)
            );
            return rows as unknown as InternalTransfer[];
        },
        enabled: !!user && !!dateFrom && !!dateTo,
    });

    // Fetch cash inflows for financing activities
    const cashInflowsQuery = useQuery({
        queryKey: ["cash-flow-details-cash-inflows", dateFrom, dateTo],
        queryFn: async () => {
            const rows = await paginateSupabase<any>((from, to) =>
                supabase
                    .from("cash_inflows" as any)
                    .select("id, inflow_number, inflow_date, amount, currency, bank_account_id, cash_flow_type, category, business_unit, source_name, reference_number")
                    .gte("inflow_date", dateFrom)
                    .lte("inflow_date", dateTo)
                    .range(from, to)
            );
            return rows as unknown as { id: string; inflow_number: string | null; inflow_date: string; amount: number; currency: string; bank_account_id: string | null; cash_flow_type: string; category: string; business_unit: string | null; source_name: string | null; reference_number: string | null }[];
        },
        enabled: !!user && !!dateFrom && !!dateTo,
    });

    const getRateForDate = useMemo(() => {
        const rates = exchangeRatesQuery.data || [];

        return (date: string, rateType: string, currencyPair: string = "USD/ETB"): number => {
            const found = rates.find(
                (r) => r.rate_type === rateType && r.currency_pair === currencyPair && r.effective_date <= date
            );
            return found ? Number(found.rate) : 1;
        };
    }, [exchangeRatesQuery.data]);

    const convertAmount = useMemo(() => {
        const PAIR_MAP: Record<string, string> = {
            USD: "USD/ETB",
            EUR: "EUR/ETB",
            GBP: "GBP/ETB",
        };

        return (
            amount: number,
            sourceCurrency: string,
            date: string,
            rateType: string = "bank_rate"
        ): number => {
            if (sourceCurrency === displayCurrency) return amount;

            // Try direct pair first (e.g. USD/EUR or EUR/USD)
            const directPair = `${sourceCurrency}/${displayCurrency}`;
            const directRate = getRateForDate(date, rateType, directPair);
            if (directRate > 0 && directRate !== 1) {
                return amount * directRate;
            }
            // Try inverse direct pair
            const inversePair = `${displayCurrency}/${sourceCurrency}`;
            const inverseRate = getRateForDate(date, rateType, inversePair);
            if (inverseRate > 0 && inverseRate !== 1) {
                return amount / inverseRate;
            }

            // Converting foreign currency to ETB
            if (displayCurrency === "ETB" && PAIR_MAP[sourceCurrency]) {
                const rate = getRateForDate(date, rateType, PAIR_MAP[sourceCurrency]);
                return rate > 0 ? amount * rate : amount;
            }

            // Converting ETB to foreign currency
            if (sourceCurrency === "ETB" && PAIR_MAP[displayCurrency]) {
                const rate = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
                return rate > 0 ? amount / rate : amount;
            }

            // Cross-currency (e.g. EUR -> USD): go through ETB
            if (PAIR_MAP[sourceCurrency] && PAIR_MAP[displayCurrency]) {
                const toEtb = getRateForDate(date, rateType, PAIR_MAP[sourceCurrency]);
                const fromEtb = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
                if (toEtb > 0 && fromEtb > 0) return (amount * toEtb) / fromEtb;
            }

            return amount;
        };
    }, [displayCurrency, getRateForDate]);

    const periodKeys = useMemo(() => {
        if (!dateFrom || !dateTo) return [];

        const from = new Date(dateFrom);
        const to = new Date(dateTo);

        if (viewPeriod === "daily") {
            return eachDayOfInterval({ start: from, end: to }).map((d) =>
                format(d, "yyyy-MM-dd")
            );
        }

        if (viewPeriod === "weekly") {
            return eachWeekOfInterval(
                { start: from, end: to },
                { weekStartsOn: 1 }
            ).map((d) => format(d, "yyyy-MM-dd"));
        }

        if (viewPeriod === "quarterly") {
            return eachQuarterOfInterval({ start: from, end: to }).map(
                (d) => `${format(d, "yyyy")}-Q${getQuarter(d)}`
            );
        }

        return eachMonthOfInterval({ start: from, end: to }).map((d) =>
            format(d, "yyyy-MM")
        );
    }, [dateFrom, dateTo, viewPeriod]);

    const dateToPeriodKey = useMemo(() => {
        return (dateStr: string): string => {
            const d = new Date(dateStr);

            if (viewPeriod === "daily") return format(d, "yyyy-MM-dd");
            if (viewPeriod === "weekly") {
                return format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");
            }
            if (viewPeriod === "quarterly") {
                return `${format(d, "yyyy")}-Q${getQuarter(d)}`;
            }

            return format(d, "yyyy-MM");
        };
    }, [viewPeriod]);

    const formatPeriodKey = useMemo(() => {
        return (key: string): string => {
            if (viewPeriod === "daily") return format(new Date(key), "MMM d");
            if (viewPeriod === "weekly") return `W ${format(new Date(key), "MMM d")}`;
            if (viewPeriod === "quarterly") return key.replace("-", " ");
            return format(new Date(`${key}-01`), "MMM yyyy");
        };
    }, [viewPeriod]);

    const getPeriodStartDate = useMemo(() => {
        return (periodKey: string): string => {
            if (viewPeriod === "daily") return periodKey;
            if (viewPeriod === "weekly") return periodKey;
            if (viewPeriod === "quarterly") {
                const [y, q] = periodKey.split("-Q");
                const month = (parseInt(q, 10) - 1) * 3 + 1;
                return `${y}-${String(month).padStart(2, "0")}-01`;
            }
            return `${periodKey}-01`;
        };
    }, [viewPeriod]);

    const openingMatrixDateFrom = useMemo(() => {
        if (!dateFrom) return dateFrom;

        const d = new Date(dateFrom);

        if (viewPeriod === "daily") return format(d, "yyyy-MM-dd");
        if (viewPeriod === "weekly") {
            return format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");
        }
        if (viewPeriod === "quarterly") {
            return format(startOfQuarter(d), "yyyy-MM-dd");
        }

        return format(startOfMonth(d), "yyyy-MM-dd");
    }, [dateFrom, viewPeriod]);

    const bankAccountIds = useMemo(
        () =>
            (bankAccountsQuery.data || [])
                .map((a: any) => a.id)
                .filter(Boolean),
        [bankAccountsQuery.data]
    );

    const openingBalancesQuery = useOpeningBalancesMatrixRange({
        bankAccountIds,
        dateFrom: openingMatrixDateFrom,
        dateTo,
        targetCurrency: displayCurrency,
    });

    const { rows, periodLabels, openingMetaByPeriod, closingMetaByPeriod } =
        useMemo(() => {
            const expenses = expensesQuery.data || [];
            const sales = salesQuery.data || [];
            const b2b = b2bQuery.data || [];
            const chipchipFees = chipchipFeesQuery.data || [];
            const categories = categoriesQuery.data || [];
            const bankAccounts = bankAccountsQuery.data || [];
            const openingData = openingBalancesQuery.data;
            const transfers = transfersQuery.data || [];
            const cashInflows = cashInflowsQuery.data || [];

            const categoryMap = new Map(categories.map((c: any) => [c.id, c]));
            const bankAccountMap = new Map(bankAccounts.map((a: any) => [a.id, a]));

            const getCashFlowType = (categoryId: string | null): string => {
                if (!categoryId) return "operating";

                let current = categoryMap.get(categoryId);
                while (current) {
                    if (current.cash_flow_type) return current.cash_flow_type;
                    if (current.parent_id) current = categoryMap.get(current.parent_id);
                    else break;
                }

                return "operating";
            };

            const getExpenseLabel = (categoryId: string | null): string => {
                if (!categoryId) return "Other Expenses";

                let current = categoryMap.get(categoryId);

                while (current) {
                    if (current.category_type === "cost_group") return current.name;

                    if (current.parent_id) {
                        const parent = categoryMap.get(current.parent_id);
                        if (parent && parent.category_type === "cost_group") {
                            return parent.name;
                        }
                        current = parent || null;
                    } else {
                        break;
                    }
                }

                const orig = categoryMap.get(categoryId);
                return orig?.name || "Other Expenses";
            };

            const getExpensePositionLabel = (categoryId: string | null): string => {
                if (!categoryId) return "Uncategorized";
                const cat = categoryMap.get(categoryId);
                return cat?.name || "Uncategorized";
            };

            const createRow = (): Map<string, number> =>
                new Map(periodKeys.map((k) => [k, 0]));

            const chipchipSalesRow = createRow();
            const chipchipFeesRow = createRow();
            const b2bSalesRow = createRow();

            for (const sale of sales as any[]) {
                const pk = dateToPeriodKey(sale.sale_date);
                const revenue = Number(sale.total_revenue) || 0;
                const converted = convertAmount(
                    revenue,
                    "ETB",
                    sale.sale_date,
                    "bank_rate"
                );
                chipchipSalesRow.set(pk, (chipchipSalesRow.get(pk) || 0) + converted);
            }

            for (const fee of chipchipFees as any[]) {
                const pk = dateToPeriodKey(fee.date);
                const revenue = Number(fee.combined_total) || 0;
                const converted = convertAmount(revenue, "ETB", fee.date, "bank_rate");
                chipchipFeesRow.set(pk, (chipchipFeesRow.get(pk) || 0) + converted);
            }

            for (const row of b2b as any[]) {
                const pk = dateToPeriodKey(row.collection_date);
                const credit = Number(row.credit_collected) || 0;
                const converted = convertAmount(
                    credit,
                    "ETB",
                    row.collection_date,
                    "bank_rate"
                );
                b2bSalesRow.set(pk, (b2bSalesRow.get(pk) || 0) + converted);
            }

            // Internal transfers FX effect
            const transfersFxRow = createRow();
            for (const t of transfers as any[]) {
                const pk = dateToPeriodKey(t.transfer_date);
                if (t.from_currency !== t.to_currency) {
                    const fromConverted = convertAmount(t.from_amount, t.from_currency, t.transfer_date, "bank_rate");
                    const toConverted = convertAmount(t.to_amount, t.to_currency, t.transfer_date, "bank_rate");
                    const fxEffect = toConverted - fromConverted;
                    transfersFxRow.set(pk, (transfersFxRow.get(pk) || 0) + fxEffect);
                }
            }

            const operatingExpenseGroups = new Map<string, Map<string, number>>();
            const investingExpenseGroups = new Map<string, Map<string, number>>();
            const financingExpenseGroups = new Map<string, Map<string, number>>();

            // Position-level detail maps: groupLabel -> categoryId -> { label, values: periodKey -> amount }
            type PositionEntry = { label: string; values: Map<string, number> };
            const operatingPositionGroups = new Map<string, Map<string, PositionEntry>>();
            const investingPositionGroups = new Map<string, Map<string, PositionEntry>>();
            const financingPositionGroups = new Map<string, Map<string, PositionEntry>>();

            for (const exp of expenses as any[]) {
                const cashFlowType = getCashFlowType(exp.category_id);
                const label = getExpenseLabel(exp.category_id);
                const positionLabel = getExpensePositionLabel(exp.category_id);
                const pk = dateToPeriodKey(exp.expense_date);
                const amount = exp.net_payment ?? exp.total_amount ?? exp.amount ?? 0;
                const currency = exp.currency || "ETB";

                const bankAccount = exp.bank_account_id
                    ? bankAccountMap.get(exp.bank_account_id)
                    : null;
                const rateType = (bankAccount as any)?.exchange_rate_type || "bank_rate";

                const converted = convertAmount(
                    amount,
                    currency,
                    exp.expense_date,
                    rateType
                );

                let targetGroup: Map<string, Map<string, number>>;
                let targetPositions: Map<string, Map<string, PositionEntry>>;
                if (cashFlowType === "investing") {
                    targetGroup = investingExpenseGroups;
                    targetPositions = investingPositionGroups;
                } else if (cashFlowType === "financing") {
                    targetGroup = financingExpenseGroups;
                    targetPositions = financingPositionGroups;
                } else {
                    targetGroup = operatingExpenseGroups;
                    targetPositions = operatingPositionGroups;
                }

                if (!targetGroup.has(label)) {
                    targetGroup.set(label, createRow());
                }
                const row = targetGroup.get(label)!;
                row.set(pk, (row.get(pk) || 0) + converted);

                // Track position-level detail, keyed by category_id so we can link to it
                if (!targetPositions.has(label)) {
                    targetPositions.set(label, new Map());
                }
                const posMap = targetPositions.get(label)!;
                const posKey = exp.category_id || "__uncategorized__";
                if (!posMap.has(posKey)) {
                    posMap.set(posKey, { label: positionLabel, values: createRow() });
                }
                const posEntry = posMap.get(posKey)!;
                posEntry.values.set(pk, (posEntry.values.get(pk) || 0) + converted);
            }

            const openingBalanceRow = createRow();
            const closingBalanceRow = createRow();

            const totalSalesRow = createRow();
            const totalOperatingRow = createRow();
            const totalInvestingRow = createRow();
            const totalFinancingRow = createRow();
            const netCashFlowRow = createRow();

            const openingMetaByPeriod = new Map<string, PeriodMeta>();
            const closingMetaByPeriod = new Map<string, PeriodMeta>();

            // Cash inflows — classified into the correct IAS 7 section by
            // category (grants/reimbursements that fund operations are operating
            // inflows, not financing). Presented gross: each is its own income
            // line; the reimbursed cost positions stay at their full amount.
            const INFLOW_CAT_LABELS: Record<string, string> = {
              capital_injection: 'Capital Injection',
              loan_proceeds: 'Loan Proceeds',
              loan_repayment: 'Loan Repayment',
              asset_sale: 'Asset Sale',
              grant: 'Grants Received',
              refund: 'Refund',
              cost_reimbursement: 'Cost Reimbursement',
              other: 'Other Inflows',
            };
            const INFLOW_BU_LABELS: Record<string, string> = {
              chipchip: 'ChipChip',
              zeami: 'Zeami',
              export: 'Export',
              grantt: 'Grantt',
              zarely: 'Zarely',
            };
            // IAS 7 classification per inflow category. Income grants / cost
            // reimbursements / refunds relate to operations; asset sales are
            // investing; owner/lender funding is financing.
            const INFLOW_SECTION: Record<string, 'operating' | 'investing' | 'financing'> = {
              cost_reimbursement: 'operating',
              refund: 'operating',
              // Grants fund the business rather than being earned trading
              // revenue. IAS 20 permits either presentation and IAS 7 leaves
              // the section to policy; we present grants received under
              // FINANCING so Net Operating stays a clean trading measure.
              grant: 'financing',
              asset_sale: 'investing',
              // Repayment of a loan WE made to a third party — return of an
              // advance/loan made (IAS 7.16(f)): investing, not financing.
              loan_repayment: 'investing',
              capital_injection: 'financing',
              loan_proceeds: 'financing',
              other: 'financing',
            };
            const operatingInflowGroups = new Map<string, Map<string, number>>();
            const investingInflowGroups = new Map<string, Map<string, number>>();
            const financingInflowGroups = new Map<string, Map<string, number>>();
            const inflowGroupsBySection = {
              operating: operatingInflowGroups,
              investing: investingInflowGroups,
              financing: financingInflowGroups,
            } as const;
            // Individual inflow records per group label, for the collapsible
            // breakdown under each income line (mirrors the expense positions).
            type InflowDetail = { pk: string; label: string; date: string; amount: number };
            const inflowDetailsByLabel = new Map<string, InflowDetail[]>();

            for (const inflow of cashInflows as any[]) {
                const pk = dateToPeriodKey(inflow.inflow_date);
                const catLabel = INFLOW_CAT_LABELS[inflow.category] || 'Other Inflows';
                const buLabel = INFLOW_BU_LABELS[inflow.business_unit as string];
                // Reimbursement-type inflows get their own line per business unit
                const isReimbursement = inflow.category === 'cost_reimbursement' || inflow.category === 'refund';
                const label = isReimbursement && buLabel ? `${buLabel} ${catLabel}` : catLabel;
                const currency = inflow.currency || 'ETB';
                const bankAccount = inflow.bank_account_id ? bankAccountMap.get(inflow.bank_account_id) : null;
                const rateType = (bankAccount as any)?.exchange_rate_type || 'bank_rate';
                const converted = convertAmount(inflow.amount, currency, inflow.inflow_date, rateType);

                const section = INFLOW_SECTION[inflow.category] ?? 'financing';
                const groups = inflowGroupsBySection[section];
                if (!groups.has(label)) groups.set(label, createRow());
                const row = groups.get(label)!;
                row.set(pk, (row.get(pk) || 0) + converted);

                // Record the individual line for the drill-down. Prefer a human
                // reference (who/what it was for), falling back to the inflow no.
                const detailLabel =
                    inflow.source_name ||
                    inflow.reference_number ||
                    inflow.inflow_number ||
                    'Cash inflow';
                if (!inflowDetailsByLabel.has(label)) inflowDetailsByLabel.set(label, []);
                inflowDetailsByLabel.get(label)!.push({
                    pk,
                    label: detailLabel,
                    date: inflow.inflow_date,
                    amount: converted,
                });
            }

            // Build the collapsible children (individual inflows) for an income
            // group, one row per record, sorted by date.
            const buildInflowChildren = (
                groupLabel: string,
                keyPrefix: string
            ): CashFlowRow[] => {
                const details = inflowDetailsByLabel.get(groupLabel);
                if (!details || !details.length) return [];
                return [...details]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((d, i) => {
                        const values = createRow();
                        values.set(d.pk, d.amount);
                        return {
                            key: `${keyPrefix}-${i}`,
                            label: `${d.label} · ${d.date}`,
                            indent: 2,
                            values,
                        };
                    });
            };

            for (const pk of periodKeys) {
                const salesTotal =
                    (chipchipSalesRow.get(pk) || 0) +
                    (chipchipFeesRow.get(pk) || 0) +
                    (b2bSalesRow.get(pk) || 0);
                totalSalesRow.set(pk, salesTotal);

                // Operating inflows (grants / cost reimbursements / refunds) are
                // operating income shown gross alongside sales.
                let opInflowTotal = 0;
                operatingInflowGroups.forEach((row) => {
                    opInflowTotal += row.get(pk) || 0;
                });
                let opTotal = 0;
                operatingExpenseGroups.forEach((row) => {
                    opTotal += row.get(pk) || 0;
                });
                totalOperatingRow.set(pk, salesTotal + opInflowTotal - opTotal);

                let invInflowTotal = 0;
                investingInflowGroups.forEach((row) => {
                    invInflowTotal += row.get(pk) || 0;
                });
                let invTotal = 0;
                investingExpenseGroups.forEach((row) => {
                    invTotal += row.get(pk) || 0;
                });
                totalInvestingRow.set(pk, invInflowTotal - invTotal);

                let finExpTotal = 0;
                financingExpenseGroups.forEach((row) => {
                    finExpTotal += row.get(pk) || 0;
                });
                let finInflowTotal = 0;
                financingInflowGroups.forEach((row) => {
                    finInflowTotal += row.get(pk) || 0;
                });
                const fxEffect = transfersFxRow.get(pk) || 0;
                totalFinancingRow.set(pk, finInflowTotal - finExpTotal);

                const netCash =
                    (salesTotal + opInflowTotal - opTotal) +
                    (invInflowTotal - invTotal) +
                    (finInflowTotal - finExpTotal + fxEffect);
                netCashFlowRow.set(pk, netCash);

                const periodStart = getPeriodStartDate(pk);
                const openingDay: ConvertedOpeningBalanceDay | undefined =
                    openingData?.converted?.get(periodStart);

                const openingAmount = openingDay?.amount ?? 0;
                openingBalanceRow.set(pk, openingAmount);
                closingBalanceRow.set(pk, openingAmount + netCash);

                const meta: PeriodMeta = {
                    isPartial: openingDay?.isPartial ?? false,

                    includedAccounts: openingDay?.includedAccounts ?? [],

                    missingFxAccountsCount: openingDay?.missingFxAccountsCount ?? 0,
                    missingFxAmountByCurrency:
                        openingDay?.missingFxAmountByCurrency ?? {},
                    missingFxAccounts: openingDay?.missingFxAccounts ?? [],

                    missingSourceAccountsCount:
                        openingDay?.missingSourceAccountsCount ?? 0,
                    missingSourceAmountByCurrency:
                        openingDay?.missingSourceAmountByCurrency ?? {},
                    missingSourceAccounts: openingDay?.missingSourceAccounts ?? [],
                };

                openingMetaByPeriod.set(pk, meta);
                closingMetaByPeriod.set(pk, meta);
            }

            const allRows: CashFlowRow[] = [];

            allRows.push({
                key: "opening",
                label: "Opening Balance",
                isTotal: true,
                values: openingBalanceRow,
            });

            allRows.push({
                key: "op-header",
                label: "OPERATING ACTIVITIES",
                isHeader: true,
                values: createRow(),
            });

            allRows.push({
                key: "chipchip-sales",
                label: "ChipChip Sales",
                indent: 1,
                values: chipchipSalesRow,
            });

            allRows.push({
                key: "chipchip-fees",
                label: "ChipChip (Delivery & Packaging Fee)",
                indent: 1,
                values: chipchipFeesRow,
            });

            allRows.push({
                key: "b2b-sales",
                label: "B2B Sales",
                indent: 1,
                values: b2bSalesRow,
            });

            allRows.push({
                key: "total-sales",
                label: "Total Sales Inflow",
                isTotal: true,
                indent: 1,
                values: totalSalesRow,
            });

            // Operating inflows (grants / cost reimbursements / refunds) — shown
            // gross as their own income lines under an OTHER OPERATING INCOME
            // parent, each collapsible to its individual inflow records.
            const sortedOpInflowGroups = [...operatingInflowGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );
            if (sortedOpInflowGroups.length) {
                allRows.push({
                    key: "op-income-header",
                    label: "OTHER OPERATING INCOME",
                    isHeader: true,
                    values: createRow(),
                });
                for (const [label, row] of sortedOpInflowGroups) {
                    allRows.push({
                        key: `op-in-${label}`,
                        label,
                        indent: 1,
                        values: row,
                        children: buildInflowChildren(label, `op-in-${label}`),
                    });
                }
            }

            const buildChildren = (
                positionMap: Map<string, Map<string, PositionEntry>>,
                groupLabel: string,
                keyPrefix: string
            ): CashFlowRow[] => {
                const positions = positionMap.get(groupLabel);
                if (!positions || positions.size <= 1) return [];
                return [...positions.entries()]
                    .sort((a, b) => a[1].label.localeCompare(b[1].label))
                    .map(([catId, entry]) => ({
                        key: `${keyPrefix}-pos-${catId}`,
                        label: entry.label,
                        indent: 2,
                        values: entry.values,
                        categoryId: catId === "__uncategorized__" ? undefined : catId,
                    }));
            };

            const sortedOpGroups = [...operatingExpenseGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );

            // When operating income lines are present, label the outflow block so
            // the expense groups don't read as if they sit under OTHER OPERATING
            // INCOME.
            if (sortedOpInflowGroups.length && sortedOpGroups.length) {
                allRows.push({
                    key: "op-expenses-header",
                    label: "OPERATING EXPENSES",
                    isHeader: true,
                    values: createRow(),
                });
            }

            for (const [label, row] of sortedOpGroups) {
                allRows.push({
                    key: `op-exp-${label}`,
                    label: `(${label})`,
                    indent: 1,
                    values: row,
                    children: buildChildren(operatingPositionGroups, label, `op-exp-${label}`),
                });
            }

            allRows.push({
                key: "net-operating",
                label: "Net Operating Cash Flow",
                isTotal: true,
                values: totalOperatingRow,
            });

            allRows.push({
                key: "inv-header",
                label: "INVESTING ACTIVITIES",
                isHeader: true,
                values: createRow(),
            });

            const sortedInvGroups = [...investingExpenseGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );

            for (const [label, row] of sortedInvGroups) {
                allRows.push({
                    key: `inv-exp-${label}`,
                    label: `(${label})`,
                    indent: 1,
                    values: row,
                    children: buildChildren(investingPositionGroups, label, `inv-exp-${label}`),
                });
            }

            // Investing inflows (e.g. asset sales) — shown gross under INVESTING,
            // each collapsible to its individual inflow records.
            const sortedInvInflowGroups = [...investingInflowGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );
            for (const [label, row] of sortedInvInflowGroups) {
                allRows.push({
                    key: `inv-in-${label}`,
                    label,
                    indent: 1,
                    values: row,
                    children: buildInflowChildren(label, `inv-in-${label}`),
                });
            }

            // FX effect on cross-currency internal transfers is reported on its
            // own line after the three activity sections (IAS 7.28), not inside
            // investing or financing.
            const hasFxEffect = [...transfersFxRow.values()].some(v => v !== 0);

            allRows.push({
                key: "net-investing",
                label: "Net Investing Cash Flow",
                isTotal: true,
                values: totalInvestingRow,
            });

            allRows.push({
                key: "fin-header",
                label: "FINANCING ACTIVITIES",
                isHeader: true,
                values: createRow(),
            });

            // Financing inflows (positive) — each collapsible to its records.
            const sortedFinInflowGroups = [...financingInflowGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );
            for (const [label, row] of sortedFinInflowGroups) {
                allRows.push({
                    key: `fin-in-${label}`,
                    label,
                    indent: 1,
                    values: row,
                    children: buildInflowChildren(label, `fin-in-${label}`),
                });
            }

            // Financing outflows (negative)
            const sortedFinGroups = [...financingExpenseGroups.entries()].sort((a, b) =>
                a[0].localeCompare(b[0])
            );

            for (const [label, row] of sortedFinGroups) {
                allRows.push({
                    key: `fin-exp-${label}`,
                    label: `(${label})`,
                    indent: 1,
                    values: row,
                    children: buildChildren(financingPositionGroups, label, `fin-exp-${label}`),
                });
            }

            allRows.push({
                key: "net-financing",
                label: "Net Financing Cash Flow",
                isTotal: true,
                values: totalFinancingRow,
            });

            if (hasFxEffect) {
                allRows.push({
                    key: "fx-effect",
                    label: "Effect of Exchange Rate Changes on Cash",
                    isTotal: true,
                    values: transfersFxRow,
                });
            }

            allRows.push({
                key: "net-cash-flow",
                label: "Net Cash Flow",
                isTotal: true,
                values: netCashFlowRow,
            });

            allRows.push({
                key: "closing",
                label: "Closing Balance",
                isTotal: true,
                values: closingBalanceRow,
            });

            const labels = periodKeys.map((k) => ({
                key: k,
                label: formatPeriodKey(k),
            }));

            return {
                rows: allRows,
                periodLabels: labels,
                openingMetaByPeriod,
                closingMetaByPeriod,
            };
        }, [
            expensesQuery.data,
            salesQuery.data,
            chipchipFeesQuery.data,
            b2bQuery.data,
            categoriesQuery.data,
            bankAccountsQuery.data,
            openingBalancesQuery.data,
            transfersQuery.data,
            cashInflowsQuery.data,
            periodKeys,
            dateToPeriodKey,
            convertAmount,
            formatPeriodKey,
            getPeriodStartDate,
        ]);

    const isLoading =
        exchangeRatesQuery.isLoading ||
        expensesQuery.isLoading ||
        categoriesQuery.isLoading ||
        salesQuery.isLoading ||
        chipchipFeesQuery.isLoading ||
        b2bQuery.isLoading ||
        bankAccountsQuery.isLoading ||
        openingBalancesQuery.isLoading ||
        transfersQuery.isLoading ||
        cashInflowsQuery.isLoading;

    return {
        rows,
        periodLabels,
        periodKeys,
        openingMetaByPeriod,
        closingMetaByPeriod,
        isLoading,
        displayCurrency,
    };
}